import React, { useState, useEffect } from 'react';
import { format, addDays } from 'date-fns';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const MilestoneForm = ({ 
  milestone = null, 
  onSave, 
  onCancel, 
  contractParties = [],
  isMobile = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    assignedParty: '',
    priority: 'medium',
    notificationEnabled: true,
    reminderDays: 7,
    dependencies: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (milestone) {
      setFormData({
        title: milestone?.title || '',
        description: milestone?.description || '',
        dueDate: milestone?.dueDate || '',
        assignedParty: milestone?.assignedParty || '',
        priority: milestone?.priority || 'medium',
        notificationEnabled: milestone?.notificationEnabled ?? true,
        reminderDays: milestone?.reminderDays || 7,
        dependencies: milestone?.dependencies || []
      });
    } else {
      // Set default due date to 30 days from today
      const defaultDueDate = format(addDays(new Date(), 30), 'yyyy-MM-dd');
      setFormData(prev => ({
        ...prev,
        dueDate: defaultDueDate
      }));
    }
  }, [milestone]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData?.dueDate) {
      newErrors.dueDate = 'Due date is required';
    } else {
      const dueDate = new Date(formData?.dueDate);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      if (dueDate < today) {
        newErrors.dueDate = 'Due date cannot be in the past';
      }
    }

    if (!formData?.assignedParty) {
      newErrors.assignedParty = 'Assigned party is required';
    }

    if (formData?.reminderDays < 1 || formData?.reminderDays > 365) {
      newErrors.reminderDays = 'Reminder days must be between 1 and 365';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSave?.(formData);
    } catch (error) {
      console.error('Error saving milestone:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'critical', label: 'Critical Priority' }
  ];

  const reminderOptions = [
    { value: 1, label: '1 day before' },
    { value: 3, label: '3 days before' },
    { value: 7, label: '1 week before' },
    { value: 14, label: '2 weeks before' },
    { value: 30, label: '1 month before' }
  ];

  return (
    <div className={`${isMobile ? 'p-4' : 'h-full flex flex-col'}`}>
      {/* Form Header */}
      <div className={`${isMobile ? 'mb-6' : 'p-6 border-b border-border'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {milestone ? 'Edit Milestone' : 'Add New Milestone'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {milestone ? 'Update milestone details and settings' : 'Create a new milestone for contract tracking'}
            </p>
          </div>
          {!isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              iconName="X"
            />
          )}
        </div>
      </div>

      {/* Form Content */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto p-6'}`}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Basic Information</h4>
            
            <div>
              <Input
                label="Milestone Title"
                placeholder="Enter milestone title"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                error={errors?.title}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                placeholder="Describe the milestone objectives and deliverables..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Scheduling */}
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Scheduling</h4>
            
            <div>
              <Input
                type="date"
                label="Due Date"
                value={formData?.dueDate}
                onChange={(e) => handleInputChange('dueDate', e?.target?.value)}
                error={errors?.dueDate}
                required
              />
            </div>

            <div>
              <Select
                label="Assigned Party"
                placeholder="Select responsible party"
                value={formData?.assignedParty}
                onValueChange={(value) => handleInputChange('assignedParty', value)}
                error={errors?.assignedParty}
                required
              >
                {contractParties?.map(party => (
                  <option key={party} value={party}>
                    {party}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Select
                label="Priority Level"
                value={formData?.priority}
                onValueChange={(value) => handleInputChange('priority', value)}
              >
                {priorityOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Notifications */}
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Notifications</h4>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                checked={formData?.notificationEnabled}
                onCheckedChange={(checked) => handleInputChange('notificationEnabled', checked)}
              />
              <div>
                <label className="text-sm font-medium text-foreground">
                  Enable Notifications
                </label>
                <p className="text-xs text-muted-foreground">
                  Receive reminders before the milestone due date
                </p>
              </div>
            </div>

            {formData?.notificationEnabled && (
              <div>
                <Select
                  label="Reminder Schedule"
                  value={formData?.reminderDays}
                  onValueChange={(value) => handleInputChange('reminderDays', parseInt(value))}
                >
                  {reminderOptions?.map(option => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          {/* Dependencies */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-foreground">Dependencies</h4>
              <Button
                type="button"
                variant="outline"
                size="xs"
                iconName="Plus"
              >
                Add Dependency
              </Button>
            </div>
            
            {formData?.dependencies?.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
                <Icon name="GitBranch" size={24} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No dependencies</p>
                <p className="text-xs text-muted-foreground">This milestone has no prerequisite milestones</p>
              </div>
            ) : (
              <div className="space-y-2">
                {formData?.dependencies?.map((dep, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-border rounded-md">
                    <span className="text-sm text-foreground">{dep}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        const newDeps = formData?.dependencies?.filter((_, i) => i !== index);
                        handleInputChange('dependencies', newDeps);
                      }}
                      iconName="X"
                      className="text-error hover:text-error"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Form Footer */}
      <div className={`${isMobile ? 'mt-6 pt-6 border-t border-border' : 'p-6 border-t border-border bg-muted/30'}`}>
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {milestone ? 'Changes will be saved to the existing milestone' : 'A new milestone will be created'}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            
            <Button
              onClick={handleSubmit}
              loading={isSubmitting}
              iconName={milestone ? "Save" : "Plus"}
            >
              {milestone ? 'Update Milestone' : 'Create Milestone'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilestoneForm;